/**
 * A pagina de funil, parametrizada por `kind`.
 *
 * As rotas /funis/carrinho e /funis/expirados sao a MESMA anatomia (hero,
 * chips de produto, quatro abas, KPIs com delta, caminho, "o que fazer agora",
 * faixa do disparo, regua, tabela por oferta) com vocabulario e regua
 * diferentes. Um componente so, com o `kind`, evita duas copias que
 * divergiriam em semanas.
 *
 * Estado que mora aqui:
 *  - `oferta`: o chip "Produto:". NAO refaz requisicao -- escolhe quais fatias
 *    entram na soma (agregacao.ts), entao a troca e instantanea e os numeros
 *    da tabela batem com os KPIs.
 *  - `estadoLeads`: o filtro de estado da aba de leads. Fica aqui (e nao na
 *    tabela) para "O que fazer agora" abrir a aba ja recortada.
 *  - `config`: a regua salva "na previa" pela aba Configuracoes. Enquanto e
 *    `null`, vale o que veio do servidor.
 *  - `foco`: toque que a aba Configuracoes deve rolar ate, quando um insight
 *    manda "ajustar este toque".
 *  - `key` = kind + empresa: trocar de empresa no cabecalho zera chip, aba e
 *    configuracao local, porque nada disso faz sentido para outra empresa.
 *
 * A aba Configuracoes fica MONTADA e escondida (`hidden`) quando outra aba
 * esta ativa: e a unica com rascunho digitado, e perder o texto ao dar uma
 * olhada nas metricas seria cruel.
 */

import { useCallback, useEffect, useState } from 'react';
import { EmptyState, ErrorPanel, RefreshingDot, SimulatedNotice } from '../../../components/PageState';
import { Badge, FilterChips, HeroBanner, LinkButton, Tabs, type ChipOption, type TabItem } from '../../../components/ui';
import { useApiResource } from '../../../lib/api';
import { useCompany } from '../../../lib/companyContext';
import { formatBRLFromCents, formatCount, formatCountCompact, formatDate, formatRatio } from '../../../lib/format';
import { describeSelection } from '../../../lib/period';
import { usePeriod } from '../../../lib/periodContext';
import type { CompanySummary } from '../../../types';
import { TUDO, gerarInsights, linhasPorOferta, recortar, resumoDoDisparo } from '../agregacao';
import { fetchFunil } from '../api';
import type { AcaoDoInsight, FunnelKind } from '../types';
import { VOCABULARIO } from '../vocabulario';
import { CaminhoDoFunil } from './CaminhoDoFunil';
import { ConfiguracaoDaRegua, type ConfigDaRegua } from './ConfiguracaoDaRegua';
import { FaixaDisparo } from './FaixaDisparo';
import { KpisDoFunil } from './KpisDoFunil';
import { MetricasDoFunil } from './MetricasDoFunil';
import { OQueFazerAgora } from './OQueFazerAgora';
import { ReguaDeToques } from './ReguaDeToques';
import { TabelaDeLeadsDoFunil } from './TabelaDeLeadsDoFunil';
import { TabelaPorOferta } from './TabelaPorOferta';

type Aba = 'visao' | 'leads' | 'config' | 'metricas';

export function PaginaDoFunil({ kind }: { readonly kind: FunnelKind }): JSX.Element {
  const { company } = useCompany();
  const v = VOCABULARIO[kind];
  const disponivel = company.funnels.includes(kind);

  useEffect(() => {
    document.title = `${v.tituloCurto} — ${company.name} · Converte Fácil`;
  }, [company.name, v.tituloCurto]);

  return (
    <section aria-labelledby="titulo-da-pagina" className="flex flex-col gap-6">
      <HeroBanner
        icon={v.icone}
        tone={kind === 'expirados' ? 'brand' : 'accent'}
        title={v.titulo}
        backTo={
          <LinkButton to="/" variant="ghost" icon="seta-esq">
            <span className="sr-only">Voltar à visão geral</span>
          </LinkButton>
        }
        badges={
          <>
            {disponivel ? (
              <Badge tone="good" dot>
                Ativo
              </Badge>
            ) : (
              <Badge tone="neutral" dot>
                Não ativado
              </Badge>
            )}
            <Badge tone={v.badgeTone} icon={v.icone}>
              {v.badgeTipo}
            </Badge>
            <Badge tone="neutral" icon="escudo">
              Só depois do {v.fato}
            </Badge>
          </>
        }
        description={
          <>
            Responsável: <strong className="text-ink">{company.contactName}</strong> · {v.descricao}
          </>
        }
        aside={
          <LinkButton to="/leads" variant="primary" icon="leads">
            Lista completa
          </LinkButton>
        }
      />

      {disponivel ? <FunilCarregado key={`${kind}:${company.id}`} kind={kind} company={company} /> : <FunilNaoAtivado kind={kind} company={company} />}
    </section>
  );
}

function FunilNaoAtivado({ kind, company }: { readonly kind: FunnelKind; readonly company: CompanySummary }): JSX.Element {
  const v = VOCABULARIO[kind];
  return (
    <EmptyState
      title={`O ${v.titulo.toLowerCase()} não está ligado para ${company.name}`}
      description={
        kind === 'expirados'
          ? 'Este funil acompanha assinaturas já vencidas — nunca avisa antes do vencimento. Ele entra em ação quando a empresa tem ao menos uma oferta recorrente e o funil de expirados ativado no cadastro.'
          : 'Este funil acompanha checkouts já abandonados (30 minutos sem pagar). Ele entra em ação quando a plataforma de checkout da empresa está ligada ao Converte Fácil.'
      }
      action={
        <LinkButton to="/configuracoes" icon="config">
          Ver configurações
        </LinkButton>
      }
    />
  );
}

function FunilCarregado({ kind, company }: { readonly kind: FunnelKind; readonly company: CompanySummary }): JSX.Element {
  const v = VOCABULARIO[kind];
  const { range, selection } = usePeriod();

  const buscar = useCallback((signal: AbortSignal) => fetchFunil(kind, company.id, range, signal), [kind, company.id, range]);
  const { state, reload } = useApiResource(buscar, 60_000);

  const [oferta, setOferta] = useState<string>(TUDO);
  const [aba, setAba] = useState<Aba>('visao');
  const [estadoLeads, setEstadoLeads] = useState<string>(TUDO);
  const [resultadosVisiveis, setResultadosVisiveis] = useState(true);
  const [config, setConfig] = useState<ConfigDaRegua | null>(null);
  const [foco, setFoco] = useState<string | null>(null);

  if (state.status === 'loading') {
    return <EsqueletoDoFunil />;
  }
  if (state.status === 'error') {
    return <ErrorPanel title={`Não foi possível carregar o ${v.titulo.toLowerCase()}`} error={state.error} onRetry={reload} />;
  }

  const data = state.data;
  const configAtual: ConfigDaRegua = config ?? { ativo: data.active, touches: data.touches };
  const metricas = recortar(data.slices, data.touches, oferta);
  const leads = oferta === TUDO ? data.leads : data.leads.filter((l) => l.offerId === oferta);
  const nomeDaOferta = data.slices.find((s) => s.offer.id === oferta)?.offer.name ?? null;
  const linhas = linhasPorOferta(kind, data.slices, data.touches);
  const insights = gerarInsights(kind, metricas, oferta === TUDO ? linhas : linhas.filter((r) => r.offer.id === oferta), leads, configAtual.touches, formatBRLFromCents, formatCount, formatRatio);

  const opcoes: ChipOption<string>[] = [
    { value: TUDO, label: 'Tudo', count: formatCountCompact(data.slices.reduce((acc, s) => acc + s.metrics.entered, 0)) },
    ...data.slices.map((s) => ({ value: s.offer.id, label: s.offer.name, count: formatCountCompact(s.metrics.entered) })),
  ];

  const abas: TabItem<Aba>[] = [
    { value: 'visao', label: 'Visão geral', icon: 'painel' },
    { value: 'leads', label: v.abaLeads, icon: v.abaLeadsIcone, count: formatCountCompact(leads.length) },
    { value: 'config', label: 'Configurações', icon: 'config' },
    { value: 'metricas', label: 'Métricas', icon: 'grafico' },
  ];

  const executarAcao = (acao: AcaoDoInsight): void => {
    switch (acao.kind) {
      case 'oferta':
        setOferta(acao.offerId);
        document.getElementById('por-oferta')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      case 'leads':
        setEstadoLeads(acao.status ?? TUDO);
        setAba('leads');
        return;
      case 'config':
        setAba('config');
        setFoco(acao.touchId);
        return;
    }
  };

  return (
    <>
      {state.simulated ? (
        <SimulatedNotice>Esta tela mostra um conjunto fictício e fixo, gerado a partir da empresa e da janela escolhidas. Nenhum número aqui vem do banco.</SimulatedNotice>
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <FilterChips label="Filtrar por produto" prefix="Produto:" options={opcoes} value={oferta} onChange={setOferta} />
        <RefreshingDot active={state.refreshing} />
      </div>

      <Tabs items={abas} value={aba} onChange={setAba} label="Seções do funil" panelId="painel-do-funil" />

      <div id="painel-do-funil" role="tabpanel" className="flex flex-col gap-6">
        {aba === 'visao' ? (
          <>
            <KpisDoFunil kind={kind} metrics={metricas} />
            <CaminhoDoFunil kind={kind} metrics={metricas} />
            <OQueFazerAgora kind={kind} insights={insights} onAcao={executarAcao} />
            <FaixaDisparo
              kind={kind}
              ativo={configAtual.ativo}
              resumo={resumoDoDisparo(metricas, data.touches)}
              resultadosVisiveis={resultadosVisiveis}
              onAlternarResultados={() => setResultadosVisiveis((r) => !r)}
              onAjustar={() => {
                setFoco(null);
                setAba('config');
              }}
            />
            <ReguaDeToques kind={kind} touches={configAtual.touches} metrics={metricas} resultadosVisiveis={resultadosVisiveis} funnelSinceAt={data.funnelSinceAt} feeBps={data.feeBps} />
            <TabelaPorOferta kind={kind} rows={linhas} selecionada={oferta} onSelecionar={setOferta} />
          </>
        ) : null}

        {aba === 'leads' ? <TabelaDeLeadsDoFunil kind={kind} leads={leads} touches={configAtual.touches} estado={estadoLeads} onEstado={setEstadoLeads} /> : null}

        <div hidden={aba !== 'config'}>
          <ConfiguracaoDaRegua kind={kind} salvo={configAtual} empresa={company.name} onSalvar={setConfig} focoToqueId={aba === 'config' ? foco : null} />
        </div>

        {aba === 'metricas' ? <MetricasDoFunil kind={kind} metrics={metricas} /> : null}
      </div>

      <p className="text-xs text-ink-subtle">
        Período de {formatDate(data.periodStart)} a {formatDate(data.periodEnd)} ({describeSelection(selection)}) · {company.name}
        {nomeDaOferta === null ? '' : ` · ${nomeDaOferta}`}
        {config === null ? '' : ' · régua ajustada na prévia'}
        {metricas.previous === null ? ' · sem período anterior para comparar' : ' · comparado com a janela imediatamente anterior, de mesmo tamanho'}
      </p>
    </>
  );
}

function EsqueletoDoFunil(): JSX.Element {
  return (
    <div className="flex flex-col gap-6" aria-hidden="true">
      <p role="status" className="sr-only">
        Carregando o funil
      </p>
      <div className="h-8 w-2/3 animate-pulse rounded-full bg-ink/5" />
      <div className="h-11 w-full max-w-xl animate-pulse rounded-xl bg-ink/5" />
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-48 animate-pulse rounded-2xl border border-line bg-surface" />
        ))}
      </div>
      <div className="h-56 animate-pulse rounded-2xl border border-line bg-surface" />
      <div className="h-40 animate-pulse rounded-2xl border border-line bg-surface" />
      <div className="h-96 animate-pulse rounded-2xl border border-line bg-surface" />
    </div>
  );
}
